import { useCallback, useMemo } from 'react'

import { Loader } from '@/components'
import { ElementDetailV2, TabConfig } from '@/components/ElementDetailV2'
import { useTabContext } from '@/context/TabContext/TabContext'
import { TableV2Provider } from '@/context/TableV2Context'
import { SqlImportButton } from '@/features/sqlImport'
import { useAppStore } from '@/hooks'
import { useDetailTabContext } from '@/pages/User/pages/Home/components/DetailTab'
import {
	NewDependenciesV2,
	NewDeploymentsV2,
	NewValidationV2,
	NewVersionsTabV2,
	PreviewV2,
} from '@/pages/User/pages/Home/components'
import { useNodeInit } from '@/pages/User/pages/Home/hooks/useNodeInit'
import { StructureDto } from '@/rtkApi/mddApiSliceGenerated'

import { ColumnsV2 } from './pages/Columns'
import { OverviewV2 } from './pages/Overview'
import { PermissionsV2 } from './pages/Permissions'
import { SourcesV2 } from './pages/Sources'
import { useViewDataContext, ViewDataProvider, ViewTabV2 } from './ViewDataContext'

// Inner component that uses the context
const ViewDetailV2Inner = () => {
	const { onSaveError } = useTabContext()
	const {
		view,
		node,
		editMode,
		dirty,
		selectedTab,
		objectTypes,
		onTabChange,
		onEdit,
		onCancel,
		onSave,
		autosaveResetKey,
	} = useViewDataContext()

	useNodeInit()

	const handleTabChange = useCallback(
		async (tab: ViewTabV2) => {
			if (editMode && dirty) {
				try {
					await onSave()
				} catch (e) {
					onSaveError(e)
					return
				}
			}
			onTabChange(tab)
		},
		[dirty, editMode, onSave, onSaveError, onTabChange],
	)

	// Tab configuration - defined once, uses generic TabConfig interface
	const tabs = useMemo<TabConfig<ViewTabV2>[]>(
		() => [
			{
				id: ViewTabV2.Overview,
				labelKey: 'TAB_OVERVIEW',
				component: <OverviewV2 />,
			},
			{
				id: ViewTabV2.Sources,
				labelKey: 'TAB_SOURCES',
				component: <SourcesV2 />,
			},
			{
				id: ViewTabV2.Columns,
				labelKey: 'TAB_COLUMNS',
				component: <ColumnsV2 />,
			},
			{
				id: ViewTabV2.Permissions,
				labelKey: 'PERMISSIONS',
				component: <PermissionsV2 />,
			},
			{
				id: ViewTabV2.Preview,
				labelKey: 'TAB_PREVIEW',
				component: node?.id ? (
					<PreviewV2
						node={node as unknown as StructureDto}
						showObjectTypes={!!objectTypes?.length}
						objectTypes={objectTypes}
					/>
				) : (
					<Loader loaded={false} />
				),
			},
			{
				id: ViewTabV2.Deployments,
				labelKey: 'TAB_DEPLOYMENTS',
				component: <NewDeploymentsV2 />,
			},
			{
				id: ViewTabV2.Validation,
				labelKey: 'TAB_VALIDATION',
				component: <NewValidationV2 />,
			},
			{
				id: ViewTabV2.Dependencies,
				labelKey: 'DEPENDENCIES',
				component: <NewDependenciesV2 />,
			},
			{
				id: ViewTabV2.Versions,
				labelKey: 'VERSIONS',
				component: <NewVersionsTabV2 />,
			},
		],
		[node, objectTypes],
	)

	if (!view) {
		return <Loader loaded={false} />
	}

	return (
		<ElementDetailV2
			tabs={tabs}
			selectedTab={selectedTab}
			onTabChange={handleTabChange}
			editMode={editMode}
			dirty={dirty}
			node={node}
			onEdit={onEdit}
			onCancel={onCancel}
			onSave={onSave}
			autosaveResetKey={autosaveResetKey}
			additionalMenuItems={editMode ? <SqlImportButton structureType="view" /> : null}
		/>
	)
}

// Wrapper that provides context; TableV2Provider enables new table styling/features for views only
export const ViewDetailV2 = () => {
	const {
		state: { node },
	} = useDetailTabContext()
	const viewRefreshNonce = useAppStore((state) =>
		state.aiSlice.viewRefreshSignal?.nodeId === node.id
			? (state.aiSlice.viewRefreshSignal?.nonce ?? 0)
			: 0,
	)

	return (
		<ViewDataProvider key={`view-v2-${node.id}-${viewRefreshNonce}`}>
			<TableV2Provider value={{ v2Ready: true }}>
				<ViewDetailV2Inner />
			</TableV2Provider>
		</ViewDataProvider>
	)
}

